import { motion, useInView } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ImageWithFallback } from "../ImageWithFallback";
import { AGENCY_PROJECTS } from "../../data/agencyProjects";

/**
 * Agency portfolio — one project at a time, cross-faded.
 *
 * One large card in the page's own colour, floating on a shadow and drifting the
 * way the service pills do (.gc-float). The screenshot sits on a gradient plate
 * that overhangs the card's left edge — so the neon hover ring is drawn behind
 * the body (.gc-pcard) rather than across the image. Copy rises in sequence
 * beside it, and a rail of bullets marks position (vertical at md+, horizontal
 * on phones — see .gc-pslide* in globals.css).
 *
 * Five ways to advance: the wheel while over the card (bounded — see the wheel
 * effect), prev/next buttons, bullets, drag/swipe, and arrow keys. Autoplay
 * carries it otherwise, pausing on hover, focus and reduced motion.
 */

const AUTOPLAY_MS = 7000;
const SWIPE_PX = 60;
/** Wheel delta that has to add up before the slider steps, and the pause after. */
const WHEEL_PX = 90;
const WHEEL_COOLDOWN_MS = 550;

// The plate behind each screenshot cycles the three brand colours.
const PLATE_GRADIENTS = [
  "linear-gradient(147deg, #01E12D 0%, #256af4 74%)",
  "linear-gradient(147deg, #256af4 0%, #f42536 74%)",
  "linear-gradient(147deg, #f42536 0%, #01E12D 74%)",
];
const PLATE_GLOWS = [
  "0 18px 40px -12px rgba(1, 225, 45, 0.45)",
  "0 18px 40px -12px rgba(37, 106, 244, 0.45)",
  "0 18px 40px -12px rgba(244, 37, 54, 0.4)",
];

export function AgencyProjects() {
  const ref = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const total = AGENCY_PROJECTS.length;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback(
    (delta: number) => setIndex((i) => (i + delta + total) % total),
    [total],
  );

  // Autoplay. Paused on hover/focus, and off entirely for reduced motion.
  useEffect(() => {
    if (paused) return;
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => go(1), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [paused, go]);

  /**
   * Wheel / trackpad over the card steps through projects.
   *
   * Bound natively because React's onWheel is passive — preventDefault there is
   * a no-op. Two rules keep it from hijacking the page: deltas must add up past
   * a threshold before a step is taken (with a cooldown after each), and at the
   * first/last project the wheel is released entirely, so scrolling past the
   * section always works. That is also why wheel does not wrap around the way
   * the dots, arrows and swipe do.
   */
  const indexRef = useRef(0);
  indexRef.current = index;

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // Bound to the element once — the accumulator and cooldown must survive
    // across steps, and re-binding per index would reset them mid-gesture.
    let acc = 0;
    let lockedUntil = 0;

    function onWheel(e: WheelEvent) {
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (delta === 0) return;
      const forward = delta > 0;
      const at = indexRef.current;

      // At either end, hand the wheel back to the page.
      if ((forward && at === total - 1) || (!forward && at === 0)) {
        acc = 0;
        return;
      }

      e.preventDefault();

      const now = performance.now();
      // Momentum after a step keeps arriving; swallow it until the pause is up.
      if (now < lockedUntil) return;
      // A change of direction restarts the count, so a flick back is immediate.
      if (acc !== 0 && Math.sign(acc) !== Math.sign(delta)) acc = 0;
      acc += delta;

      if (Math.abs(acc) >= WHEEL_PX) {
        acc = 0;
        lockedUntil = now + WHEEL_COOLDOWN_MS;
        setIndex(Math.min(total - 1, Math.max(0, at + (forward ? 1 : -1))));
      }
    }

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [total]);

  // Drag / swipe. Pointer capture is fine here — the card body is not a link.
  const dragX = useRef<number | null>(null);
  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    dragX.current = e.clientX;
  }
  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (dragX.current === null) return;
    const dx = e.clientX - dragX.current;
    dragX.current = null;
    if (Math.abs(dx) > SWIPE_PX) go(dx < 0 ? 1 : -1);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
    if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
  }

  // Update the spotlight position on the card's neon border.
  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--x", `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty("--y", `${e.clientY - rect.top}px`);
  }

  return (
    <section ref={ref} className="relative w-full px-4 md:px-10 lg:px-16 py-16 md:py-24 flex flex-col items-center gap-4">
      <p className="font-mono text-[12px] md:text-[14px] tracking-[3px] uppercase text-[#666]">What we are proud of</p>
      <h2 className="font-black text-[48px] md:text-[96px] leading-[1.05] tracking-[-2.4px] text-[#141414] text-center">
        Our Projects
      </h2>
      <p className="text-[16px] md:text-[20px] leading-[1.6] text-[#666] text-center max-w-[640px]">
        Live platforms we have designed, built or advised on — each one still running in the wild.
      </p>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="mt-12 w-full max-w-[1320px] md:pl-[80px]"
      >
        {/* Drift lives on its own element so it never fights the entrance
            transform above — same trick as the service pills (.gc-float). */}
        <div className="gc-float" style={{ "--gc-float-dur": "9s" } as React.CSSProperties}>
          <div
            ref={cardRef}
            role="group"
            aria-roledescription="carousel"
            aria-label="Client projects"
            tabIndex={0}
            onKeyDown={onKeyDown}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerMove={handlePointerMove}
            onPointerEnter={() => setPaused(true)}
            onPointerLeave={() => { setPaused(false); dragX.current = null; }}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            className="gc-pcard relative touch-pan-y select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#01E12D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#e7f1f1]"
          >
            <div className="relative z-[1] grid rounded-[30px] bg-[#e7f1f1] p-6 md:min-h-[max(540px,50vh)] md:p-12 md:pr-16 shadow-[0px_28px_50px_-18px_rgba(0,62,91,0.35)]">
              {AGENCY_PROJECTS.map((project, i) => {
                const active = i === index;
                return (
                  <div
                    key={project.url}
                    className="gc-pslide flex flex-col items-center md:flex-row"
                    data-active={active}
                    aria-hidden={!active}
                  >
                    {/* Screenshot on its gradient plate — overhangs the card at md+,
                        which is why the neon ring is drawn behind the body. The
                        plate is 16:10, the shape the screenshots are captured at,
                        so the whole page shows with nothing cropped away. */}
                    <div
                      className="gc-pslide-img relative aspect-[16/10] w-full shrink-0 overflow-hidden rounded-[24px] md:w-[560px] md:-translate-x-[80px] lg:w-[640px]"
                      style={{
                        backgroundImage: PLATE_GRADIENTS[i % PLATE_GRADIENTS.length],
                        boxShadow: PLATE_GLOWS[i % PLATE_GLOWS.length],
                      }}
                    >
                      <ImageWithFallback
                        src={project.image}
                        alt={active ? `${project.name} — screenshot` : ""}
                        className="h-full w-full object-contain object-center"
                      />
                      {/* Brand wash — only seen mid-change. It clears once a slide
                          settles (see .gc-pslide-tint) so the screenshot reads clean;
                          during the cross-fade the outgoing and incoming washes overlap
                          and you catch the colour as you move between projects. */}
                      <span
                        aria-hidden="true"
                        className="gc-pslide-tint pointer-events-none absolute inset-0 rounded-[24px]"
                        style={{ backgroundImage: PLATE_GRADIENTS[i % PLATE_GRADIENTS.length] }}
                      />
                    </div>

                    <div className="gc-pslide-content flex min-w-0 flex-col gap-5 pt-6 text-center md:-ml-[40px] md:pt-0 md:text-left">
                      <span className="font-mono text-[12px] md:text-[13px] tracking-[2px] uppercase text-[#666]">
                        {String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                        <span className="mx-2 text-[#b3c2c2]">·</span>
                        {project.display}
                      </span>

                      <h3 className="font-black text-[30px] md:text-[48px] leading-[1.05] tracking-[-1.2px] text-[#141414]">
                        {project.name}
                      </h3>

                      <p className="text-[15px] md:text-[19px] leading-[1.65] text-[#666]">{project.summary}</p>

                      <div className="flex flex-wrap justify-center gap-2 md:justify-start">
                        {project.tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full border border-[rgba(0,0,0,0.12)] px-4 py-1.5 text-[12px] font-medium text-[#141414]"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      <div>
                        <a
                          href={project.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          tabIndex={active ? undefined : -1}
                          className="group inline-flex items-center gap-2 rounded-full bg-[#141414] px-8 py-4 font-bold text-[13px] tracking-[1.5px] uppercase text-[#e7f1f1] transition-all duration-300 hover:bg-[#01E12D] hover:text-[#141414] hover:shadow-[0_0_28px_rgba(1,225,45,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#01E12D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#e7f1f1]"
                        >
                          Visit site
                          <svg
                            className="h-[14px] w-[14px] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                            viewBox="0 0 14 14"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M3 11L11 3M11 3H5M11 3V9"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Bullet rail */}
              <div className="z-[2] mt-8 flex justify-center gap-2 md:absolute md:right-8 md:top-1/2 md:mt-0 md:-translate-y-1/2 md:flex-col md:gap-3">
                {AGENCY_PROJECTS.map((project, i) => (
                  <button
                    key={project.url}
                    type="button"
                    onClick={() => setIndex(i)}
                    aria-current={i === index}
                    aria-label={`Show ${project.name}`}
                    className="gc-pbullet focus:outline-none focus-visible:ring-2 focus-visible:ring-[#01E12D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#e7f1f1]"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Arrows live outside the card so they never sit on top of a screenshot. */}
      <div className="mt-10 flex items-center gap-4">
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="Previous project"
          className="grid h-14 w-14 place-items-center rounded-full border-[1.5px] border-[#141414] text-[22px] font-bold text-[#141414] transition-all duration-150 hover:bg-[#141414] hover:text-[#e7f1f1] active:scale-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#01E12D]"
        >
          ‹
        </button>
        <span className="font-mono text-[13px] tracking-[1px] text-[#666]">
          {String(index + 1).padStart(2, "0")}&nbsp;/&nbsp;{String(total).padStart(2, "0")}
        </span>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="Next project"
          className="grid h-14 w-14 place-items-center rounded-full border-[1.5px] border-[#141414] text-[22px] font-bold text-[#141414] transition-all duration-150 hover:bg-[#141414] hover:text-[#e7f1f1] active:scale-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#01E12D]"
        >
          ›
        </button>
      </div>

      <p className="mt-2 font-mono text-[11px] tracking-[1px] uppercase text-[#9aa5a5]">
        scroll over the card&nbsp;·&nbsp;swipe&nbsp;·&nbsp;← →
      </p>
    </section>
  );
}
